import { useRef, useState } from "react";
import { isAllowedUploadFile, ALLOWED_UPLOAD_EXTENSIONS, MAX_UPLOAD_FILES, MAX_UPLOAD_BYTES } from "../shared/validate.js";
import { UploadIcon, TrashIcon, FileTextIcon } from "../components/Icons.js";

export interface UploadItem {
  file: File;
  error?: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUpload({ items, onChange }: { items: UploadItem[]; onChange: (items: UploadItem[]) => void }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    const combined = [...items];
    for (const file of incoming) {
      if (combined.length >= MAX_UPLOAD_FILES) break;
      const check = isAllowedUploadFile(file.name, file.size);
      combined.push({ file, error: check.valid ? undefined : check.message });
    }
    onChange(combined);
  }

  function removeAt(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div>
      <div
        className="file-upload"
        data-dragover={dragOver ? "true" : "false"}
        role="button"
        tabIndex={0}
        aria-label="Bestanden uploaden — klik of sleep bestanden hierheen"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e: any) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e: any) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e: any) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <UploadIcon size={28} className="text-sage" />
        <p className="mb-0 mt-2">
          <strong>Sleep bestanden hierheen</strong> of{" "}
          <span className="text-orange" style={{ textDecoration: "underline" }}>
            bestanden kiezen
          </span>
        </p>
        <p className="file-upload__hint">
          {ALLOWED_UPLOAD_EXTENSIONS.join(", ").toUpperCase()} — max {Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))}MB per bestand,
          max {MAX_UPLOAD_FILES} bestanden
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="visually-hidden"
          accept={ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(",")}
          onChange={(e: any) => addFiles(e.target.files)}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      {items.length > 0 && (
        <ul className="file-list">
          {items.map((item, i) => (
            <li className="file-list__item" key={`${item.file.name}-${i}`}>
              <span className="flex items-center gap-2">
                <FileTextIcon size={16} />
                {item.file.name} <span className="text-tiny text-muted">({formatSize(item.file.size)})</span>
              </span>
              {item.error ? (
                <span className="validation-message" style={{ marginTop: 0 }}>
                  {item.error}
                </span>
              ) : (
                <span className="file-list__status">Klaar om te verzenden</span>
              )}
              <button type="button" className="file-list__remove" onClick={() => removeAt(i)} aria-label={`${item.file.name} verwijderen`}>
                <TrashIcon size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
